'use client';

import * as React from 'react';
import Link from 'next/link';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/atoms/primitives/dropdown-menu';
import { cn } from '@/components/atoms/primitives/utils';
import { categoryDisplayLabel } from '@/lib/city-home/categoryLabelCase';
import { CATEGORIES } from '@/utils/categoryConstants';
import { CategoriesMobileSheet } from './CategoriesMobileSheet';
import { defaultCategoryHref } from './categoryHref';

/** Hover-intent delay before opening on desktop pointer (ms). */
const HOVER_INTENT_DELAY_MS = 100;

/**
 * Grace period before closing on mouse leave (ms). Gives the pointer time to
 * transit from trigger to menu (or back) without dismissing the panel.
 */
const HOVER_CLOSE_GRACE_MS = 120;

/** Media query matching coarse / no-hover input (touch). */
const TOUCH_MEDIA_QUERY = '(hover: none)';

/**
 * Media query matching `< md` viewports per the Tailwind default breakpoint
 * (md = 768px, so the mobile gate is ≤ 767px). FRE-823 mandates the bottom
 * sheet on this surface even when the device has a real pointer (e.g.
 * resized desktop browser at 375px).
 */
const MOBILE_MAX_WIDTH_QUERY = '(max-width: 767px)';

/**
 * Subscribe to a `MediaQueryList` and return its current `matches` value,
 * re-rendering on changes. SSR-safe: returns `false` on first render to
 * match the server output and avoid hydration mismatches; flips to the
 * true value in the post-mount effect.
 */
function useMatchMedia(query: string): boolean {
  const [matches, setMatches] = React.useState<boolean>(false);
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia(query);
    const onChange = (): void => {
      setMatches(mql.matches);
    };
    setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

export { defaultCategoryHref };

export interface CategoriesDropdownProps {
  /**
   * Builds the destination href for a category slug. Defaults to
   * {@link defaultCategoryHref} (the marketing-city editorial page, correct on
   * `<city>.loci.city`). The consumer app header (`AppHeader`) overrides this
   * with an in-app filtered-discover deep link
   * (`/<city>/discover?category=<slug>`) because `app.loci.city` has no
   * category page.
   */
  buildCategoryHref?: (slug: string) => string;
  /** Slug of the currently active category, if any (sets `data-active`). */
  activeSlug?: string;
  /** Optional className applied to the trigger button. */
  className?: string;
  /**
   * Controlled-open override — primarily for tests and Storybook.
   * When omitted, internal state + hover-intent / click drives the open state.
   */
  defaultOpen?: boolean;
}

/**
 * Shared trigger button classes — kept identical between the desktop
 * dropdown and the mobile sheet variants so the visual chrome never shifts
 * when the viewport crosses the `md` boundary.
 */
const TRIGGER_CLASSNAME = cn(
  'inline-flex min-h-[44px] desktop:min-h-0 items-center gap-1.5 rounded-full px-3 py-2.5 text-sm font-medium',
  'text-foreground/90 transition-colors',
  'hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  'data-[state=open]:text-foreground',
);

/**
 * CategoriesDropdown molecule.
 *
 * Primary-nav megamenu/dropdown for the six LOCI categories (Music, Sports,
 * Food, Tech, Art, Drinks).
 *
 * Two render variants share the same trigger button:
 *   - **Desktop** (pointer + ≥ md viewport): Radix `DropdownMenu` with
 *     100ms hover-intent open and 120ms close grace. Glass-pill panel per
 *     v1.1.0 spec.
 *   - **Mobile** (touch / `(hover: none)` / ≤ 767px): {@link CategoriesMobileSheet}
 *     bottom sheet via the shared `Sheet` primitive (FRE-823). Hover timers
 *     are inert; the trigger is a plain button that toggles the sheet's
 *     open state. v1.1.0 backdrop spec (rgba(15,20,31,0.65) + blur 6px).
 *
 * The mobile gate is `useMobileVariant = hover:none ∨ max-width:767px` so a
 * resized desktop browser at < md still gets the bottom sheet (the AC
 * explicitly tests at 320px and 375px regardless of pointer).
 */
export function CategoriesDropdown({
  buildCategoryHref = defaultCategoryHref,
  activeSlug,
  className,
  defaultOpen,
}: CategoriesDropdownProps): React.JSX.Element {
  const [open, setOpen] = React.useState<boolean>(defaultOpen ?? false);
  const hoverTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const isTouch = useMatchMedia(TOUCH_MEDIA_QUERY);
  const isNarrow = useMatchMedia(MOBILE_MAX_WIDTH_QUERY);
  const useMobileVariant = isTouch || isNarrow;

  // Clear any pending hover-intent timer on unmount or when switching
  // variants (e.g. orientation change or window resize crosses the < md
  // boundary while a desktop hover-open / close-grace timer is pending).
  // Without the `useMobileVariant` dep, a stale desktop timer could fire
  // after the mobile sheet has mounted and toggle `open` unexpectedly.
  React.useEffect(() => {
    if (useMobileVariant && hoverTimerRef.current !== null) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    return () => {
      if (hoverTimerRef.current !== null) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
    };
  }, [useMobileVariant]);

  const clearHoverTimer = React.useCallback((): void => {
    if (hoverTimerRef.current !== null) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const handleMouseEnter = React.useCallback((): void => {
    // Mobile variant has no hover-intent — pointer events are a no-op so
    // the bottom sheet only opens on click / touch.
    if (useMobileVariant) {
      return;
    }
    clearHoverTimer();
    hoverTimerRef.current = setTimeout(() => {
      setOpen(true);
      hoverTimerRef.current = null;
    }, HOVER_INTENT_DELAY_MS);
  }, [useMobileVariant, clearHoverTimer]);

  const handleMouseLeave = React.useCallback((): void => {
    if (useMobileVariant) {
      return;
    }
    clearHoverTimer();
    hoverTimerRef.current = setTimeout(() => {
      setOpen(false);
      hoverTimerRef.current = null;
    }, HOVER_CLOSE_GRACE_MS);
  }, [useMobileVariant, clearHoverTimer]);

  /* ------------------------------------------------------------------ */
  /*  Mobile bottom-sheet variant                                        */
  /* ------------------------------------------------------------------ */

  if (useMobileVariant) {
    return (
      <>
        <button
          type="button"
          data-testid="categories-dropdown-trigger"
          data-state={open ? 'open' : 'closed'}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls="categories-mobile-sheet"
          onClick={() => setOpen((prev) => !prev)}
          // `group` enables the `group-data-[state=open]:rotate-180` selector
          // on the chevron child below — the desktop variant gets the same
          // effect for free because Radix's `DropdownMenuTrigger` propagates
          // `data-state` to its descendants, but the plain `<button>` we use
          // for the mobile sheet does not, so we rely on the group selector.
          className={cn('group', TRIGGER_CLASSNAME, className)}
        >
          Categories
          <svg
            aria-hidden="true"
            viewBox="0 0 12 12"
            className="size-3 transition-transform group-data-[state=open]:rotate-180"
          >
            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
          </svg>
        </button>
        <CategoriesMobileSheet
          open={open}
          onOpenChange={setOpen}
          buildCategoryHref={buildCategoryHref}
          activeSlug={activeSlug}
        />
      </>
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Desktop dropdown variant (unchanged from FRE-818)                  */
  /* ------------------------------------------------------------------ */

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        data-testid="categories-dropdown-trigger"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={cn(TRIGGER_CLASSNAME, className)}
      >
        Categories
        <svg
          aria-hidden="true"
          viewBox="0 0 12 12"
          className="size-3 transition-transform data-[state=open]:rotate-180"
        >
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={8}
        data-testid="categories-dropdown-content"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={cn(
          // Glass-pill panel per v1.1.0 spec
          'min-w-[220px] rounded-xl border p-1.5 shadow-lg',
          'border-white/[0.12] bg-white/[0.06] text-foreground',
          '[backdrop-filter:blur(14px)] [-webkit-backdrop-filter:blur(14px)]',
        )}
      >
        {CATEGORIES.map((category) => {
          const Icon = category.icon;
          const isActive = activeSlug === category.slug;
          return (
            <DropdownMenuItem
              key={category.slug}
              asChild
              data-active={isActive ? 'true' : undefined}
              data-testid={`categories-dropdown-item-${category.slug}`}
              className={cn(
                'group relative flex min-h-[44px] desktop:min-h-0 cursor-pointer items-center gap-3 rounded-md px-3',
                'text-sm transition-colors',
                'data-[active=true]:font-semibold',
              )}
            >
              <Link href={buildCategoryHref(category.slug)}>
                <Icon className="size-4 shrink-0" aria-hidden="true" />
                <span className="flex-1">{categoryDisplayLabel(category.name)}</span>
                <span
                  aria-hidden="true"
                  className={cn(
                    'pointer-events-none absolute bottom-1 left-3 right-3 h-px origin-left scale-x-0',
                    'bg-accent transition-transform duration-150',
                    'group-hover:scale-x-100 group-focus-visible:scale-x-100',
                    'group-data-[active=true]:scale-x-100',
                  )}
                />
              </Link>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
